import api from '../../utils/api';

/**
 * Utility to convert the UI's string time range to actual start and end ISO dates.
 */
export const getTimeRangeDates = (timeRange: string, customStart?: string, customEnd?: string) => {
  let end = new Date();
  let start = new Date();

  if (timeRange === 'Custom Range' && customStart && customEnd) {
    const s = new Date(customStart);
    const e = new Date(customEnd);
    
    // Previous period should be the same length before the start date
    const diffTime = Math.abs(e.getTime() - s.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const prevE = new Date(s);
    prevE.setDate(prevE.getDate() - 1);
    
    const prevS = new Date(prevE);
    prevS.setDate(prevS.getDate() - diffDays);
    
    return {
      start: s.toISOString(),
      end: e.toISOString(),
      prevStart: prevS.toISOString(),
      prevEnd: prevE.toISOString()
    };
  }

  switch (timeRange) {
    case 'Today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'Yesterday':
      start.setDate(end.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case 'This Month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'Last Month':
      start.setDate(1);
      start.setMonth(start.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      // 'end' is now for 'Previous Month', so it's the last day of the previous month
      end.setTime(start.getTime());
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'Last 7 Days':
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case 'Last 30 Days':
      start.setDate(end.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case 'Last 3 Months':
      start.setMonth(end.getMonth() - 3);
      break;
    case 'Last 12 Months':
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      start.setDate(end.getDate() - 30);
  }

  // Prevent querying data prior to BigQuery integration on July 6, 2026
  const DATA_START_DATE = new Date('2026-07-06T00:00:00.000Z');
  if (start < DATA_START_DATE) {
    start = new Date(DATA_START_DATE);
  }
  if (end < DATA_START_DATE) {
    // If the entire selected range is before data existed, clamp it entirely so it predictably returns empty/0s
    end = new Date(DATA_START_DATE);
  }

  // Calculate matching previous duration for accurate comparison
  const durationMs = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 1); // 1 ms before start
  const prevStart = new Date(prevEnd.getTime() - durationMs); // Match exact duration backwards

  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start: formatDate(start),
    end: formatDate(end),
    prevStart: formatDate(prevStart),
    prevEnd: formatDate(prevEnd)
  };
};

export const getBillingOverview = async (startDate: string, endDate: string, prevStartDate: string, prevEndDate: string, projectId: string = 'vedika-health', serviceId?: string, resourceName?: string) => {
  let url = `/master-admin/billing-analytics/overview?startDate=${startDate}&endDate=${endDate}&prevStartDate=${prevStartDate}&prevEndDate=${prevEndDate}&projectId=${projectId}`;
  if (serviceId) url += `&serviceId=${serviceId}`;
  if (resourceName) url += `&resourceName=${resourceName}`;
  const response = await api.get(url);
  return response.data.data;
};

export const getBillingTrends = async (startDate: string, endDate: string, projectId: string = 'vedika-health', serviceId?: string, resourceName?: string) => {
  let url = `/master-admin/billing-analytics/trends?startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`;
  if (serviceId) url += `&serviceId=${serviceId}`;
  if (resourceName) url += `&resourceName=${resourceName}`;
  const response = await api.get(url);
  return response.data.data;
};

export const getBillingServices = async (startDate: string, endDate: string, projectId: string = 'vedika-health', serviceId?: string, resourceName?: string) => {
  let url = `/master-admin/billing-analytics/services?startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`;
  if (serviceId) url += `&serviceId=${serviceId}`;
  if (resourceName) url += `&resourceName=${resourceName}`;
  const response = await api.get(url);
  return response.data.data;
};

export const getBillingResources = async (startDate: string, endDate: string, projectId: string = 'vedika-health', limit: number = 50, serviceId?: string, resourceName?: string) => {
  let url = `/master-admin/billing-analytics/resources?startDate=${startDate}&endDate=${endDate}&projectId=${projectId}&limit=${limit}`;
  if (serviceId) url += `&serviceId=${serviceId}`;
  if (resourceName) url += `&resourceName=${resourceName}`;
  const response = await api.get(url);
  return response.data.data;
};

export const getServiceHistory = async (id: string, start: string, end: string, projectId?: string) => {
  const response = await api.get(`/master-admin/billing-analytics/services/${id}/history`, {
    params: { startDate: start, endDate: end, projectId }
  });
  return response.data.data;
};

export const getResourceHistory = async (id: string, start: string, end: string, projectId?: string) => {
  // Use encodeURIComponent for resource names as they often contain slashes and spaces
  const response = await api.get(`/master-admin/billing-analytics/resources/${encodeURIComponent(id)}/history`, {
    params: { startDate: start, endDate: end, projectId }
  });
  return response.data.data;
};

export const getBillingHistory = async (startDate: string, endDate: string, projectId: string = 'vedika-health', serviceId?: string, resourceName?: string) => {
  let url = `/master-admin/billing-analytics/history?startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`;
  if (serviceId) url += `&serviceId=${serviceId}`;
  if (resourceName) url += `&resourceName=${resourceName}`;
  const response = await api.get(url);
  return response.data.data;
};

export const getBillingProjects = async () => {
  const response = await api.get('/master-admin/billing-analytics/projects');
  return response.data.data;
};
